import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserForm from '../components/UserForm';
import ProjectsGrid from '../components/ProjectsGrid';
import Admin from './Admin';
import { t } from '../i18n';

// Utility to get API base URL depending on environment
const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  // Use relative URL for production
  return '';
};

const AccountPage = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likedProjects, setLikedProjects] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/create-user');
      return;
    }

    // Fetch user data
    fetch(`${getApiBaseUrl()}/api/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((response) => {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate('/create-user');
          return null;
        }
        if (!response.ok) throw new Error('API response not ok');
        return response.json();
      })
      .then((data) => {
        if (data) {
          setUserData(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError('Erreur lors du chargement du compte');
        setLoading(false);
        console.error('Error fetching user data:', err);
      });

    // Fetch liked projects
    fetch(`${getApiBaseUrl()}/api/users/liked-projects`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) throw new Error('API response not ok');
        return response.json();
      })
      .then((data) => {
        setLikedProjects(data.projects || []);
      })
      .catch((err) => {
        console.error('Error fetching liked projects:', err);
      });
  }, [navigate]);

  const handleUpdateUser = async (updatedData) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${getApiBaseUrl()}/api/users/update`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updatedData),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Erreur lors de la mise à jour du compte');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token'); // Clear the token
    navigate('/create-user'); // Redirect to create-user page
  };



  if (loading) {
    return <p>{t('loading') || 'Chargement...'}</p>;
  }

  if (error) {
    return <p style={{ color: 'red' }}>{error}</p>;
  }

  // Handler to unlike a project
  const handleDislike = async (projectId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/projects/${projectId}/dislike`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Erreur lors du retrait du like');
      // Remove project from likedProjects state
      setLikedProjects((prev) => prev.filter((p) => p.id !== projectId));
    } catch (err) {
      alert('Erreur lors du retrait du like');
    }
  };

  return (
    <>
      <UserForm
        initialData={userData}
        onSubmit={handleUpdateUser}
        isEditMode={true}
        onLogout={handleLogout}
      />
      {/* Liked Projects Section */}
      {likedProjects.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <h2 style={{ textAlign: 'center', fontWeight: 700, fontSize: 26, marginBottom: 24 }}>
            Projets que vous avez aimés
          </h2>
          <ProjectsGrid
            projects={likedProjects}
            hideIcons={false}
            likedProjectIds={likedProjects.map((p) => p.id)}
            onProjectClick={(idOrIdSlug) => {
              let id = idOrIdSlug;
              let slug = '';
              if (typeof idOrIdSlug === 'string' && idOrIdSlug.includes('-')) {
                navigate(`/project/${idOrIdSlug}`);
                return;
              }
              const project = likedProjects.find((p) => p.id === idOrIdSlug || p.id === Number(idOrIdSlug));
              if (project) {
                slug = project.name
                  .toString()
                  .normalize('NFD')
                  .replace(/\p{Diacritic}/gu, '')
                  .replace(/[^\w\s-]/g, '')
                  .trim()
                  .replace(/\s+/g, '-')
                  .replace(/-+/g, '-')
                  .toLowerCase();
                navigate(`/project/${id}-${slug}`);
              } else {
                navigate(`/project/${id}`);
              }
            }}
            onDislike={handleDislike}
          />
        </section>
      )}
      {/* Admin panel for admin users */}
      {userData && userData.admin === 1 && (
        <section style={{ marginTop: 40 }}>
          <Admin />
        </section>
      )}
      {/* Language switcher removed; use FooterBar for language selection */}
    </>
  );
};

export default AccountPage;